import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { HandbookTrainingNode, HandbookTrainingNodeKind, HandbookTrainingOutlineChapter } from '../../../types/handbookTraining';
import {
  folderDepthLabel,
  handbookTrainingBreadcrumbTitles,
  handbookTrainingParentFolderOptions,
} from '../../../utils/handbookTrainingTree';
import {
  createHandbookTrainingNode,
  moveHandbookTrainingNode,
  nextSortOrderAmongSiblings,
  updateHandbookTrainingNode,
  type SaveHandbookTrainingNodeInput,
} from '../../../utils/handbookTrainingFirestore';
import { normalizeHandbookTrainingSlug } from '../../../utils/handbookTrainingAccess';
import {
  fetchLoomOembedMetadata,
  suggestHandbookTrainingAgentMetadata,
  tagsFromList,
} from '../../../utils/handbookTrainingAgentMetadata';

function parseTags(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map(s => s.trim())
    .filter(Boolean);
}

function tagsToString(tags: string[] | undefined): string {
  return (tags || []).join(', ');
}

type AgentFieldKey =
  | 'slug'
  | 'recordedAt'
  | 'durationSec'
  | 'routes'
  | 'features'
  | 'apis'
  | 'staleIf'
  | 'outline';

type Props = {
  mode: 'create' | 'edit';
  kind: HandbookTrainingNodeKind;
  parentId: string | null;
  parentDepth: number;
  node: HandbookTrainingNode | null;
  allNodes: HandbookTrainingNode[];
  userId: string;
  onSaved: (nodeId: string) => void;
  onCancel: () => void;
};

const HandbookTrainingEditor: React.FC<Props> = ({
  mode,
  kind,
  parentId,
  parentDepth,
  node,
  allNodes,
  userId,
  onSaved,
  onCancel,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loomUrl, setLoomUrl] = useState('');
  const [published, setPublished] = useState(false);
  const [slug, setSlug] = useState('');
  const [recordedAt, setRecordedAt] = useState('');
  const [durationSec, setDurationSec] = useState('');
  const [routes, setRoutes] = useState('');
  const [features, setFeatures] = useState('');
  const [apis, setApis] = useState('');
  const [staleIf, setStaleIf] = useState('');
  const [outline, setOutline] = useState<HandbookTrainingOutlineChapter[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agentOpen, setAgentOpen] = useState(false);
  const [loomMetaBusy, setLoomMetaBusy] = useState(false);
  const [locationParentId, setLocationParentId] = useState<string | null>(parentId);
  const touchedAgent = useRef(new Set<AgentFieldKey>());
  const loomFetchKey = useRef('');

  const existingSlugs = useMemo(
    () =>
      allNodes
        .filter(n => n.kind === 'video' && n.id !== node?.id)
        .map(n => n.slug || '')
        .filter(Boolean),
    [allNodes, node?.id],
  );

  const parentOptions = useMemo(
    () =>
      handbookTrainingParentFolderOptions(
        allNodes,
        kind === 'folder' && node ? node.id : undefined,
      ),
    [allNodes, kind, node],
  );

  const breadcrumbTitles = useMemo(
    () => handbookTrainingBreadcrumbTitles(allNodes, locationParentId),
    [allNodes, locationParentId],
  );

  useEffect(() => {
    setLocationParentId(parentId);
  }, [mode, node, parentId]);

  useEffect(() => {
    touchedAgent.current = new Set();
    loomFetchKey.current = '';
    if (mode === 'edit' && node) {
      setTitle(node.title);
      setDescription(node.description || '');
      setLoomUrl(node.loomUrl || '');
      setPublished(node.published);
      setSlug(node.slug || '');
      setRecordedAt(node.recordedAt || '');
      setDurationSec(node.durationSec != null ? String(node.durationSec) : '');
      setRoutes(tagsToString(node.covers?.routes));
      setFeatures(tagsToString(node.covers?.features));
      setApis(tagsToString(node.covers?.apis));
      setStaleIf(tagsToString(node.staleIf));
      setOutline(node.outline?.length ? [...node.outline] : []);
      if (node.slug) touchedAgent.current.add('slug');
      if (node.recordedAt) touchedAgent.current.add('recordedAt');
      if (node.durationSec != null) touchedAgent.current.add('durationSec');
      if (node.covers?.routes?.length) touchedAgent.current.add('routes');
      if (node.covers?.features?.length) touchedAgent.current.add('features');
      if (node.covers?.apis?.length) touchedAgent.current.add('apis');
      if (node.staleIf?.length) touchedAgent.current.add('staleIf');
      if (node.outline?.length) touchedAgent.current.add('outline');
    } else {
      setTitle('');
      setDescription('');
      setLoomUrl('');
      setPublished(false);
      setSlug('');
      setRecordedAt('');
      setDurationSec('');
      setRoutes('');
      setFeatures('');
      setApis('');
      setStaleIf('');
      setOutline([]);
    }
    setError(null);
  }, [mode, node]);

  const applySuggestion = (
    suggestion: ReturnType<typeof suggestHandbookTrainingAgentMetadata>,
    opts?: { forceEmptyOnly?: boolean },
  ) => {
    const forceEmptyOnly = opts?.forceEmptyOnly !== false;
    const shouldSet = (key: AgentFieldKey, current: string) => {
      if (touchedAgent.current.has(key)) return false;
      if (forceEmptyOnly && current.trim()) return false;
      return true;
    };

    setSlug(prev => (shouldSet('slug', prev) ? suggestion.slug : prev));
    setRecordedAt(prev =>
      shouldSet('recordedAt', prev) ? suggestion.recordedAt || '' : prev,
    );
    if (suggestion.durationSec != null) {
      setDurationSec(prev =>
        shouldSet('durationSec', prev) ? String(suggestion.durationSec) : prev,
      );
    }
    setRoutes(prev =>
      shouldSet('routes', prev) ? tagsFromList(suggestion.routes) : prev,
    );
    setFeatures(prev =>
      shouldSet('features', prev) ? tagsFromList(suggestion.features) : prev,
    );
    setApis(prev => (shouldSet('apis', prev) ? tagsFromList(suggestion.apis) : prev));
    setStaleIf(prev =>
      shouldSet('staleIf', prev) ? tagsFromList(suggestion.staleIf) : prev,
    );
    setOutline(prev => {
      if (touchedAgent.current.has('outline')) return prev;
      if (forceEmptyOnly && prev.length > 0) return prev;
      return suggestion.outline.map(ch => ({ ...ch }));
    });
  };

  useEffect(() => {
    if (kind !== 'video') return;
    const suggestion = suggestHandbookTrainingAgentMetadata({
      title,
      description,
      breadcrumbTitles,
      loomUrl,
      existingSlugs,
      currentSlug: node?.slug,
    });
    applySuggestion(suggestion);
  }, [kind, title, description, breadcrumbTitles, loomUrl, existingSlugs, node?.slug]);

  useEffect(() => {
    if (kind !== 'video') return;
    const trimmed = loomUrl.trim();
    if (!trimmed || loomFetchKey.current === trimmed) return;
    loomFetchKey.current = trimmed;
    let cancelled = false;
    setLoomMetaBusy(true);
    void (async () => {
      const meta = await fetchLoomOembedMetadata(trimmed);
      if (cancelled) return;
      setLoomMetaBusy(false);
      if (!meta) return;
      const suggestion = suggestHandbookTrainingAgentMetadata({
        title: meta.title?.trim() || title,
        description,
        breadcrumbTitles,
        loomUrl: trimmed,
        existingSlugs,
        currentSlug: node?.slug,
        loomDurationSec: meta.durationSec,
        loomRecordedAt: meta.recordedAt,
      });
      applySuggestion(suggestion);
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, loomUrl, title, description, breadcrumbTitles, existingSlugs, node?.slug]);

  const markTouched = (key: AgentFieldKey) => {
    touchedAgent.current.add(key);
  };

  const buildInput = (): SaveHandbookTrainingNodeInput => {
    const suggestion = suggestHandbookTrainingAgentMetadata({
      title,
      description,
      breadcrumbTitles,
      loomUrl,
      existingSlugs,
      currentSlug: slug || node?.slug,
    });
    const resolvedSlug =
      slug.trim() ||
      suggestion.slug ||
      normalizeHandbookTrainingSlug(title);
    const dur = parseInt(durationSec, 10);
    const resolvedDuration =
      Number.isFinite(dur) && dur > 0 ? dur : suggestion.durationSec;
    return {
      kind,
      parentId: locationParentId,
      title,
      description,
      loomUrl: kind === 'video' ? loomUrl : undefined,
      published,
      slug: kind === 'video' ? resolvedSlug : undefined,
      recordedAt: kind === 'video' ? recordedAt.trim() || suggestion.recordedAt : undefined,
      durationSec: kind === 'video' ? resolvedDuration : undefined,
      covers:
        kind === 'video'
          ? {
              routes: parseTags(routes).length ? parseTags(routes) : suggestion.routes,
              features: parseTags(features).length ? parseTags(features) : suggestion.features,
              apis: parseTags(apis).length ? parseTags(apis) : suggestion.apis,
            }
          : undefined,
      outline:
        kind === 'video'
          ? outline.length
            ? outline
            : suggestion.outline
          : undefined,
      staleIf: kind === 'video' ? (parseTags(staleIf).length ? parseTags(staleIf) : suggestion.staleIf) : undefined,
      updatedByUserId: userId,
    };
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const input = buildInput();
      if (mode === 'edit' && node) {
        if ((node.parentId ?? null) !== (locationParentId ?? null)) {
          await moveHandbookTrainingNode(node.id, locationParentId, allNodes, userId);
        }
        await updateHandbookTrainingNode(node.id, input);
        onSaved(node.id);
      } else {
        const sortOrder = nextSortOrderAmongSiblings(allNodes, locationParentId);
        const id = await createHandbookTrainingNode(input, sortOrder);
        onSaved(id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const addOutlineRow = () => {
    markTouched('outline');
    setOutline(prev => [...prev, { id: `ch-${prev.length + 1}`, title: '', t: 0 }]);
  };

  const refreshAgentMetadata = () => {
    touchedAgent.current = new Set();
    const suggestion = suggestHandbookTrainingAgentMetadata({
      title,
      description,
      breadcrumbTitles,
      loomUrl,
      existingSlugs,
      currentSlug: node?.slug,
    });
    applySuggestion(suggestion, { forceEmptyOnly: false });
  };

  const depthLabel = kind === 'folder' ? folderDepthLabel(parentDepth) : 'Video';

  return (
    <div className="people-handbook-training-editor">
      <h3 className="people-handbook-training-editor-title">
        {mode === 'create' ? `New ${depthLabel.toLowerCase()}` : `Edit ${node?.title || ''}`}
      </h3>
      <div className="ops-modal-body people-hub-form">
        <div className="ops-modal-row">
          <label className="ops-modal-label" htmlFor="ht-title">
            Title
          </label>
          <input
            id="ht-title"
            className="ops-modal-input"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </div>
        <div className="ops-modal-row">
          <label className="ops-modal-label" htmlFor="ht-location">
            Location in library
          </label>
          <select
            id="ht-location"
            className="ops-modal-input"
            value={locationParentId ?? ''}
            onChange={e => setLocationParentId(e.target.value ? e.target.value : null)}
          >
            {parentOptions.map(opt => (
              <option key={opt.value ?? '__root__'} value={opt.value ?? ''}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="ops-modal-row">
          <label className="ops-modal-label" htmlFor="ht-desc">
            Description
          </label>
          <textarea
            id="ht-desc"
            className="ops-modal-textarea"
            rows={2}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </div>
        {kind === 'video' && (
          <>
            <div className="ops-modal-row">
              <label className="ops-modal-label" htmlFor="ht-loom">
                Loom URL
              </label>
              <input
                id="ht-loom"
                className="ops-modal-input"
                value={loomUrl}
                onChange={e => {
                  loomFetchKey.current = '';
                  setLoomUrl(e.target.value);
                }}
                placeholder="https://www.loom.com/share/…"
              />
              {loomMetaBusy && (
                <p className="people-handbook-training-agent-hint">Loading Loom metadata…</p>
              )}
            </div>
            <label className="people-handbook-training-check">
              <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
              Published (visible in library and agent catalog)
            </label>
            <p className="people-handbook-training-agent-hint">
              Agent catalog fields update from the title, folder path, and Loom link. Expand to review or override.
            </p>
            <button
              type="button"
              className="people-handbook-training-agent-toggle"
              onClick={() => setAgentOpen(v => !v)}
            >
              Agent catalog metadata {agentOpen ? '▾' : '▸'}
            </button>
            {agentOpen && (
              <div className="people-handbook-training-agent-panel">
                <div className="people-handbook-training-agent-toolbar">
                  <button
                    type="button"
                    className="pm-btn pm-btn-ghost pm-btn-sm"
                    onClick={refreshAgentMetadata}
                  >
                    Regenerate from title &amp; path
                  </button>
                </div>
                <div className="ops-modal-row">
                  <label className="ops-modal-label" htmlFor="ht-slug">
                    Slug (agent id)
                  </label>
                  <input
                    id="ht-slug"
                    className="ops-modal-input"
                    value={slug}
                    onChange={e => {
                      markTouched('slug');
                      setSlug(e.target.value);
                    }}
                    placeholder={normalizeHandbookTrainingSlug(title) || 'travel-planner-board'}
                  />
                </div>
                <div className="ops-modal-grid">
                  <div className="ops-modal-row">
                    <label className="ops-modal-label" htmlFor="ht-recorded">
                      Recorded
                    </label>
                    <input
                      id="ht-recorded"
                      type="date"
                      className="ops-modal-date"
                      value={recordedAt}
                      onChange={e => {
                        markTouched('recordedAt');
                        setRecordedAt(e.target.value);
                      }}
                    />
                  </div>
                  <div className="ops-modal-row">
                    <label className="ops-modal-label" htmlFor="ht-dur">
                      Duration (sec)
                    </label>
                    <input
                      id="ht-dur"
                      type="number"
                      min={0}
                      className="ops-modal-input"
                      value={durationSec}
                      onChange={e => {
                        markTouched('durationSec');
                        setDurationSec(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="ops-modal-row">
                  <label className="ops-modal-label" htmlFor="ht-routes">
                    Covers — routes (comma)
                  </label>
                  <input
                    id="ht-routes"
                    className="ops-modal-input"
                    value={routes}
                    onChange={e => {
                      markTouched('routes');
                      setRoutes(e.target.value);
                    }}
                  />
                </div>
                <div className="ops-modal-row">
                  <label className="ops-modal-label" htmlFor="ht-features">
                    Covers — features
                  </label>
                  <input
                    id="ht-features"
                    className="ops-modal-input"
                    value={features}
                    onChange={e => {
                      markTouched('features');
                      setFeatures(e.target.value);
                    }}
                  />
                </div>
                <div className="ops-modal-row">
                  <label className="ops-modal-label" htmlFor="ht-apis">
                    Covers — APIs
                  </label>
                  <input
                    id="ht-apis"
                    className="ops-modal-input"
                    value={apis}
                    onChange={e => {
                      markTouched('apis');
                      setApis(e.target.value);
                    }}
                  />
                </div>
                <div className="ops-modal-row">
                  <label className="ops-modal-label" htmlFor="ht-stale">
                    Stale if
                  </label>
                  <textarea
                    id="ht-stale"
                    className="ops-modal-textarea"
                    rows={2}
                    value={staleIf}
                    onChange={e => {
                      markTouched('staleIf');
                      setStaleIf(e.target.value);
                    }}
                    placeholder="travelAgentApi major/minor, …"
                  />
                </div>
                <div className="people-handbook-training-outline">
                  <div className="people-handbook-training-outline-head">
                    <span className="ops-modal-label">Outline chapters</span>
                    <button type="button" className="pm-btn pm-btn-ghost pm-btn-sm" onClick={addOutlineRow}>
                      Add chapter
                    </button>
                  </div>
                  {outline.map((row, i) => (
                    <div key={`${row.id}-${i}`} className="people-handbook-training-outline-row">
                      <input
                        className="ops-modal-input"
                        value={row.id}
                        onChange={e => {
                          markTouched('outline');
                          const next = [...outline];
                          next[i] = { ...next[i], id: e.target.value };
                          setOutline(next);
                        }}
                        placeholder="id"
                      />
                      <input
                        className="ops-modal-input"
                        value={row.title}
                        onChange={e => {
                          markTouched('outline');
                          const next = [...outline];
                          next[i] = { ...next[i], title: e.target.value };
                          setOutline(next);
                        }}
                        placeholder="Title"
                      />
                      <input
                        type="number"
                        min={0}
                        className="ops-modal-input"
                        value={row.t}
                        onChange={e => {
                          markTouched('outline');
                          const next = [...outline];
                          next[i] = { ...next[i], t: parseInt(e.target.value, 10) || 0 };
                          setOutline(next);
                        }}
                        placeholder="t (sec)"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {kind === 'folder' && (
          <label className="people-handbook-training-check">
            <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
            Published (show in navigation)
          </label>
        )}
        {error && <p className="people-handbook-training-error">{error}</p>}
        <div className="people-hub-form-actions">
          <button type="button" className="pm-btn pm-btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="pm-btn pm-btn-primary" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HandbookTrainingEditor;
